<template>
	<b-form
		class="new-event-hotel-search"
		:class="`new-event-hotel-search--${variant}`"
		@submit.prevent="submit"
	>
		<header class="new-event-hotel-search__header">
			<h2>
				<span class="new-event-hotel-search__desktop-title">{{searchTitle}}</span>
				<span class="new-event-hotel-search__mobile-title">{{mobileTitle}}</span>
			</h2>
			<p>{{translation.hotelSearchObs}}</p>
		</header>

		<div class="new-event-hotel-search__fields">
			<div class="new-event-hotel-search__field new-event-hotel-search__field--date">
				<span class="new-event-hotel-search__mobile-label">{{mobileMessages.checkin}}</span>
				<date-picker
					class="new-event-hotel-search__date"
					:value="localCriteria.checkin"
					:initialDefaultDate="initialDefaultDate"
					:placeholder="translation.checkinDate"
					required
					@input="updateCriteria('checkin', $event)"
				/>
			</div>

			<div class="new-event-hotel-search__field new-event-hotel-search__field--date">
				<span class="new-event-hotel-search__mobile-label">{{mobileMessages.checkout}}</span>
				<date-picker
					class="new-event-hotel-search__date"
					:value="localCriteria.checkout"
					:initialDefaultDate="initialDefaultDate"
					:placeholder="translation.checkoutDate"
					required
					@input="updateCriteria('checkout', $event)"
				/>
			</div>

			<label class="new-event-hotel-search__quantity new-event-hotel-search__quantity--adults">
				<span class="new-event-hotel-search__quantity-label new-event-hotel-search__quantity-label--desktop">
					{{translation.guests}}
				</span>
				<span class="new-event-hotel-search__quantity-label new-event-hotel-search__quantity-label--mobile">
					{{mobileAdultLabel}}
				</span>
				<div class="new-event-hotel-search__select-wrap">
					<b-form-select
						:value="localCriteria.adultsQty"
						:options="guestOptions"
						class="new-event-hotel-search__select"
						required
						@input="updateCriteria('adultsQty', Number($event))"
					/>
				</div>
			</label>

			<label class="new-event-hotel-search__quantity new-event-hotel-search__quantity--rooms">
				<span class="new-event-hotel-search__quantity-label">
					{{translation.rooms}}
				</span>
				<div class="new-event-hotel-search__select-wrap">
					<b-form-select
						:value="localCriteria.roomsQty"
						:options="roomOptions"
						class="new-event-hotel-search__select"
						required
						@input="updateCriteria('roomsQty', Number($event))"
					/>
				</div>
			</label>

			<button type="submit" class="new-event-hotel-search__button">
				<span class="new-event-hotel-search__button-label--desktop">
					{{lowercaseLabel(translation.continueButton)}}
				</span>
				<span class="new-event-hotel-search__button-label--mobile">
					{{mobileSubmitLabel}}
				</span>
				<span v-if="variant === 'compact'" class="new-event-hotel-search__button-arrows" aria-hidden="true"></span>
			</button>
		</div>
	</b-form>
</template>

<script>
import DatePicker from '../shared/DatePicker.vue';

export default {
	components: {
		DatePicker,
	},
	props: {
		criteria: {
			type: Object,
			required: true,
		},
		initialDefaultDate: {
			type: String,
			required: true,
		},
		translation: {
			type: Object,
			required: true,
		},
		variant: {
			type: String,
			default: 'wide',
		},
	},
	data() {
		return {
			localCriteria: Object.assign({}, this.criteria),
		};
	},
	computed: {
		guestOptions() {
			return this.buildArrayOfNumbers(4);
		},
		roomOptions() {
			return this.buildArrayOfNumbers(6);
		},
		mobileAdultLabel() {
			const labels = {
				1: this.mobileMessages.single,
				2: this.mobileMessages.double,
				3: this.mobileMessages.triple,
				4: this.mobileMessages.quadruple,
			};
			return labels[Number(this.localCriteria.adultsQty)] || this.translation.guests;
		},
		mobileMessages() {
			return this.translation.mobileSearch || {};
		},
		mobileSubmitLabel() {
			if (this.variant === 'compact') return this.mobileMessages.changeSearch;
			return this.lowercaseLabel(this.translation.continueButton);
		},
		mobileTitle() {
			return this.variant === 'compact'
				? this.mobileMessages.resultTitle
				: this.searchTitle;
		},
		searchTitle() {
			return this.mobileMessages.searchTitle;
		},
	},
	watch: {
		criteria: {
			deep: true,
			handler(newValue) {
				this.localCriteria = Object.assign({}, newValue);
			},
		},
	},
	methods: {
		buildArrayOfNumbers(number) {
			let numberArray = [];
			for (let index = 1; index <= number; index++) {
				numberArray.push(index);
			}
			return numberArray;
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		submit() {
			this.$emit('search', Object.assign({}, this.localCriteria));
		},
		updateCriteria(field, value) {
			this.localCriteria = Object.assign({}, this.localCriteria, {
				[field]: value,
			});
			this.$emit('change', Object.assign({}, this.localCriteria));
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-hotel-search {
	box-sizing: border-box;
	background: #66acc6;
	color: #ffffff;
}

.new-event-hotel-search__header {
	display: flex;
	align-items: center;
	gap: 25px;
	margin-bottom: 58px;
}

.new-event-hotel-search__header h2,
.new-event-hotel-search__header p {
	margin: 0;
}

.new-event-hotel-search__header h2 {
	font-size: 28px;
	font-weight: 700;
	line-height: 25px;
}

.new-event-hotel-search__mobile-title,
.new-event-hotel-search__mobile-label,
.new-event-hotel-search__quantity-label--mobile,
.new-event-hotel-search__button-label--mobile,
.new-event-hotel-search__button-arrows {
	display: none;
}

.new-event-hotel-search__header p {
	max-width: 744px;
	font-size: 15px;
	font-weight: 400;
	line-height: 25px;
}

.new-event-hotel-search__fields {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	width: 100%;
	min-width: 0;
	gap: 22px 2%;
}

.new-event-hotel-search__field--date {
	flex: 0 0 241px;
	min-width: 0;
}

.new-event-hotel-search__date {
	width: 100%;
	min-width: 0;
}

.new-event-hotel-search__date /deep/ .datetime-input,
.new-event-hotel-search__select {
	box-sizing: border-box;
	width: 100%;
	height: 73px !important;
	border: 1px solid rgba(0, 0, 0, .2);
	border-radius: 10px;
	background-color: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
	outline: none;
	box-shadow: none;
}

.new-event-hotel-search__date /deep/ .datetime-input {
	padding: 0 62px 0 25px;
}

.new-event-hotel-search__date /deep/ .datetime-input::placeholder {
	color: #5a5a5a;
	opacity: 1;
}

.new-event-hotel-search__date /deep/ .calendar-icon {
	width: 32px;
	right: 26px;
	bottom: 18px;
}

.new-event-hotel-search__quantity {
	display: flex;
	align-items: center;
	gap: 15px;
	margin: 0;
	color: #ffffff;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
}

.new-event-hotel-search__select-wrap {
	position: relative;
	width: 104px;
}

.new-event-hotel-search__select-wrap::before {
	content: "";
	position: absolute;
	top: 0;
	right: 49px;
	z-index: 1;
	width: 1px;
	height: 72px;
	background: rgba(0, 0, 0, .1);
}

.new-event-hotel-search__select-wrap::after {
	content: "";
	position: absolute;
	top: 29px;
	right: 19px;
	z-index: 1;
	width: 14px;
	height: 14px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	pointer-events: none;
	transform: rotate(45deg) translateY(-4px);
}

.new-event-hotel-search__select {
	padding: 0 54px 0 21px;
	background-image: none;
	appearance: none;
}

.new-event-hotel-search__button {
	box-sizing: border-box;
	flex: 0 1 252px;
	max-width: 100%;
	height: 73px;
	border: 1px solid #e89127;
	border-radius: 9px;
	background: #e89127;
	color: #f8f8f8;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 21px;
	text-align: center;
	cursor: pointer;
}

.new-event-hotel-search--wide {
	width: 90%;
	max-width: 1449px;
	min-height: 306px;
	margin: 0 auto;
	padding: 75px 5.8% 76px;
	border-radius: 35px;
}

.new-event-hotel-search--wide .new-event-hotel-search__fields {
	flex-wrap: nowrap;
	justify-content: space-between;
	gap: 0;
}

.new-event-hotel-search--wide .new-event-hotel-search__field--date {
	flex: 0 1 19%;
}

.new-event-hotel-search--wide .new-event-hotel-search__quantity {
	flex: 0 1 15.7%;
	min-width: 0;
	gap: 3%;
}

.new-event-hotel-search--wide .new-event-hotel-search__quantity-label {
	flex: 1 1 45%;
	min-width: 0;
}

.new-event-hotel-search--wide .new-event-hotel-search__select-wrap {
	flex: 0 1 52%;
	width: auto;
	min-width: 0;
}

.new-event-hotel-search--wide .new-event-hotel-search__select-wrap::before {
	right: 47%;
}

.new-event-hotel-search--wide .new-event-hotel-search__select-wrap::after {
	right: 18%;
}

.new-event-hotel-search--wide .new-event-hotel-search__select {
	padding-right: 52%;
	padding-left: 20%;
}

.new-event-hotel-search--wide .new-event-hotel-search__button {
	flex: 0 1 19.8%;
	width: 19.8%;
	margin-left: 0;
}

.new-event-hotel-search--compact {
	width: 329px;
	min-height: 626px;
	padding: 39px 44px 56px;
	border-radius: 35px;
}

.new-event-hotel-search--compact .new-event-hotel-search__header {
	display: block;
	margin-bottom: 58px;
}

.new-event-hotel-search--compact .new-event-hotel-search__header h2 {
	margin-bottom: 5px;
}

.new-event-hotel-search--compact .new-event-hotel-search__header p {
	max-width: 214px;
}

.new-event-hotel-search--compact .new-event-hotel-search__fields {
	display: flex;
	flex-wrap: wrap;
	gap: 16px 33px;
}

.new-event-hotel-search--compact .new-event-hotel-search__field--date,
.new-event-hotel-search--compact .new-event-hotel-search__button {
	flex: 0 0 100%;
	width: 100%;
}

.new-event-hotel-search--compact .new-event-hotel-search__quantity {
	display: flex;
	flex: 0 0 104px;
	flex-direction: column;
	align-items: flex-start;
	gap: 9px;
	font-size: 15px;
	line-height: 18px;
}

.new-event-hotel-search--compact .new-event-hotel-search__select-wrap {
	width: 104px;
}

.new-event-hotel-search--compact .new-event-hotel-search__button {
	margin-top: 24px;
}

@media (max-width: 1400px) {
	.new-event-hotel-search--wide .new-event-hotel-search__date /deep/ .datetime-input {
		padding-right: 50px;
		padding-left: 16px;
		font-size: 15px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__date /deep/ .calendar-icon {
		width: 28px;
		right: 15px;
		bottom: 20px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__quantity,
	.new-event-hotel-search--wide .new-event-hotel-search__select,
	.new-event-hotel-search--wide .new-event-hotel-search__button {
		font-size: 15px;
	}
}

@media (max-width: 1200px) {
	.new-event-hotel-search--wide {
		padding-right: 5%;
		padding-left: 5%;
	}
}

@media (min-width: 901px) and (max-width: 1100px) {
	.new-event-hotel-search--wide {
		padding-top: 50px;
		padding-bottom: 50px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__header {
		margin-bottom: 35px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__fields {
		flex-wrap: wrap;
		gap: 22px 0;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__field--date {
		flex-basis: 48%;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__quantity,
	.new-event-hotel-search--wide .new-event-hotel-search__button {
		flex-basis: 31%;
		width: 31%;
	}
}

@media (max-width: 900px) {
	.new-event-hotel-search--wide,
	.new-event-hotel-search--compact {
		width: 100%;
		min-height: 0;
		padding: 32px 24px 36px;
		border-radius: 24px;
	}

	.new-event-hotel-search__desktop-title,
	.new-event-hotel-search__button-label--desktop {
		display: none;
	}

	.new-event-hotel-search__mobile-title,
	.new-event-hotel-search__mobile-label,
	.new-event-hotel-search__quantity-label--mobile,
	.new-event-hotel-search__button-label--mobile {
		display: inline;
	}

	.new-event-hotel-search__quantity-label--desktop {
		display: none;
	}

	.new-event-hotel-search__header,
	.new-event-hotel-search--compact .new-event-hotel-search__header {
		display: block;
		margin-bottom: 28px;
	}

	.new-event-hotel-search__header h2 {
		margin-bottom: 8px;
		font-size: 24px;
		line-height: 29px;
	}

	.new-event-hotel-search__fields,
	.new-event-hotel-search--compact .new-event-hotel-search__fields {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
	}

	.new-event-hotel-search__date,
	.new-event-hotel-search__button,
	.new-event-hotel-search--compact .new-event-hotel-search__date,
	.new-event-hotel-search--compact .new-event-hotel-search__button {
		width: 100%;
		flex-basis: auto;
	}

	.new-event-hotel-search__field--date {
		flex: 0 0 100%;
		width: 100%;
	}

	.new-event-hotel-search__quantity {
		display: flex;
		flex: 0 0 100%;
		flex-direction: column;
		align-items: flex-start;
		gap: 9px;
	}

	.new-event-hotel-search__select-wrap,
	.new-event-hotel-search--compact .new-event-hotel-search__select-wrap {
		width: 100%;
	}

	.new-event-hotel-search--wide {
		max-width: 366px;
		min-height: 550px;
		padding: 28px 28px 31px;
		border-radius: 35px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__header {
		margin-bottom: 9px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__header h2 {
		margin-bottom: 0;
		font-size: 28px;
		line-height: 25px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__header p {
		max-width: 214px;
		font-size: 15px;
		line-height: 25px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__fields {
		flex-direction: column;
		align-items: stretch;
		gap: 21px 16px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__field--date,
	.new-event-hotel-search--wide .new-event-hotel-search__button {
		flex: 0 0 100%;
		width: 100%;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__quantity {
		flex: 0 0 auto;
		min-width: 0;
		width: 100%;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__quantity-label {
		flex: none;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__date /deep/ .datetime-input {
		height: 73px !important;
		padding-right: 62px;
		padding-left: 25px;
		font-size: 18px;
		line-height: 18px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__date /deep/ .calendar-icon {
		width: 32px;
		right: 22px;
		bottom: 18px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__quantity {
		font-size: 18px;
		line-height: 18px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__select-wrap {
		flex: 0 0 auto;
		width: 100%;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__select-wrap::before {
		right: 48px;
		height: 64px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__select-wrap::after {
		top: 25px;
		right: 19px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__select {
		height: 64px;
		padding: 0 54px 0 21px;
		font-size: 18px;
	}

	.new-event-hotel-search--wide .new-event-hotel-search__button {
		flex: 0 0 73px;
		height: 73px;
		margin-top: 13px;
		font-size: 18px;
		line-height: 21px;
	}

	.new-event-hotel-search--compact {
		max-width: 366px;
		min-height: 170px;
		padding: 20px 12px 15px;
		border-radius: 18px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__header {
		margin-bottom: 12px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__header h2 {
		margin-bottom: 0;
		font-size: 20px;
		line-height: 25px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__header p {
		display: none;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__fields {
		align-items: end;
		gap: 8px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__field--date {
		flex: 1 1 0;
		width: auto;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__mobile-label,
	.new-event-hotel-search--compact .new-event-hotel-search__quantity-label--mobile {
		display: block;
		margin-bottom: 8px;
		color: #ffffff;
		font-size: 16px;
		font-weight: 400;
		line-height: 16px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__date /deep/ .datetime-input {
		height: 35px !important;
		padding: 0 25px 0 6px;
		font-size: 13px;
		line-height: 18px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__date /deep/ .calendar-icon {
		width: 18px;
		right: 4px;
		bottom: 7px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__quantity {
		display: block;
		flex: 0 0 58px;
		gap: 0;
		font-size: 16px;
		line-height: 16px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__quantity--rooms {
		display: none;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__quantity--adults .new-event-hotel-search__select-wrap {
		width: 58px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__select-wrap::before {
		right: 23px;
		height: 35px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__select-wrap::after {
		top: 12px;
		right: 7px;
		width: 9px;
		height: 9px;
		border-right-width: 2px;
		border-bottom-width: 2px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__select {
		height: 35px;
		padding: 0 25px 0 11px;
		border-radius: 10px;
		font-size: 14px;
		line-height: 18px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__button {
		display: inline-flex;
		flex: 0 0 236px;
		align-items: center;
		gap: 8px;
		max-width: 100%;
		width: 236px;
		height: 25px;
		margin-top: 0;
		padding: 0;
		border: 0;
		background: transparent;
		color: #ffffff;
		font-size: 14px;
		font-weight: 700;
		line-height: 25px;
		text-align: left;
		box-shadow: none;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__button-arrows {
		position: relative;
		display: inline-block;
		width: 17px;
		height: 11px;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__button-arrows::before,
	.new-event-hotel-search--compact .new-event-hotel-search__button-arrows::after {
		content: "";
		position: absolute;
		top: 2px;
		width: 8px;
		height: 8px;
		border-right: 3px solid #ffffff;
		border-bottom: 3px solid #ffffff;
		transform: rotate(-45deg);
	}

	.new-event-hotel-search--compact .new-event-hotel-search__button-arrows::before {
		left: 0;
	}

	.new-event-hotel-search--compact .new-event-hotel-search__button-arrows::after {
		left: 8px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/HotelReservation.vue